import apiClient from '../lib/api-client';
import { convertFiltersToServerFormat, type ServerFilter } from '../lib/utils';
import type { 
  User, 
  UsersResponse, 
  UserFilters, 
  UserCreateRequest, 
  UserUpdateRequest,
  ServerFiltersParams
} from '../modules/school/types';

export interface GetUsersParams extends UserFilters {
  page?: number;
  per_page?: number;
}

class UserService {
  private readonly baseUrl = '/user';

  private convertFiltersToServerFormat(filters: UserFilters): ServerFilter[] {
    const fieldMappings = {
      type: { field: 'type', operator: 'eq' as const },
      search: { field: 'name', operator: 'like' as const },
      status: { field: 'status', operator: 'eq' as const },
      grade: { field: 'grade', operator: 'eq' as const },
      class: { field: 'class', operator: 'eq' as const },
      classification: { field: 'classification', operator: 'eq' as const },
      unit: { field: 'unit', operator: 'eq' as const },
      teacher: { field: 'teacher', operator: 'eq' as const },
      school: { field: 'schoolId', operator: 'eq' as const }
    };

    return convertFiltersToServerFormat(filters, fieldMappings);
  }

  async getUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
    const { type, search, status, grade, class: classFilter, classification, unit, teacher, school, page, per_page, ...otherParams } = params;
    
    // Convert client filters to server format
    const filters = this.convertFiltersToServerFormat({ 
      type, 
      search, 
      status, 
      grade, 
      class: classFilter, 
      classification, 
      unit, 
      teacher,
      school
    });
    
    const serverParams: ServerFiltersParams = {
      ...otherParams,
    };

    // Chỉ thêm filters nếu có filter nào được set
    if (filters.length > 0) {
      serverParams.filters = filters;
    }

    // Backend đọc pagination lồng (`criteria.pagination.page`). Gửi phẳng khiến
    // UserController::search hiểu nhầm toàn bộ body và coi chính khoá `filters`
    // là một field để lọc `like`. Xem schoolService/classService.
    if (page !== undefined && per_page !== undefined) {
      serverParams.pagination = { page, per_page };
    }

    const response = await apiClient.post(`${this.baseUrl}/search`, serverParams);

    // Backend trả về Laravel paginator; chuẩn hoá về UsersResponse để các hook
    // đọc được `.data` và `.pagination`.
    const paginator = response.data?.data ?? {};
    return {
      data: paginator.data || [],
      pagination: {
        page: paginator.current_page || 1,
        per_page: paginator.per_page || per_page || 15,
        total: paginator.total || 0,
        totalPages: paginator.last_page || 1,
      },
    };
  }

  async getUser(id: string): Promise<User> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data?.data;
  }

  // plain_password chỉ có khi BE tự sinh mật khẩu lúc tạo
  async createUser(data: UserCreateRequest): Promise<User & { plain_password?: string }> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data?.data;
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data?.data;
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async toggleUserStatus(id: string): Promise<User> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);
    return response.data?.data;
  }

  async downloadStudentTemplate(): Promise<any> {
    const response = await apiClient.get(`${this.baseUrl}/template/student`, {
      responseType: 'blob'
    });
    return response;
  }

  async previewImport(formData: FormData): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/preview-import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async importUsers(formData: FormData): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export const userService = new UserService();
export default userService;
